import React, { useCallback, useState } from 'react';

/**
 * Odeljak ekrana koji se sklapa.
 *
 * ZAŠTO POSTOJI
 *   Poslovođin ekran je imao jedanaest odeljaka jedan pod drugim, sve otvoreno.
 *   Da bi saznao ima li posla, morao je da skroluje kroz sve — a devet od tih
 *   jedanaest ga se tog jutra nisu ticala. Sklapanje ne skriva posao: brojka na
 *   sklopljenom odeljku nosi boju, pa se vidi izdaleka.
 *
 * NE IZMIŠLJA NOVO RASKLAPANJE
 *   Koristi `disc__head` / `disc__caret` iz rečnika delova. U CSS-u stoji
 *   napomena zašto: rasklapanje je bilo napisano na sedam mesta, pa se
 *   nalazilo slučajno.
 *
 * STANJE JE SPOLJA
 *   Odeljak ne pamti sam je li otvoren. Pamti ga ekran (<see cref="useSections"/>),
 *   iz dva razloga: da se stanje sačuva između otvaranja ekrana, i da nešto sa
 *   strane — recimo brojka u sažetku — može da otvori odeljak i skoči na njega.
 */
interface Props {
  /** Stalna oznaka. Po njoj se pamti stanje i po njoj se skače na odeljak. */
  id: string;

  title: string;

  /**
   * Koliko redova unutra čeka ruku. Nula se ne prikazuje.
   *
   * Ovo NIJE broj redova nego broj poslova: odeljak „Odakle roba ide" ume da
   * ima deset redova od kojih devet ima odluku. Brojka koja broji redove bi
   * tvrdila da ima deset poslova.
   */
  count?: number;

  /** Traka uz levu ivicu: `todo` kad čeka čoveka, `alert` kad gori. */
  tone?: 'todo' | 'alert';

  /** Sitno uz naslov, npr. „poslednje dve nedelje". */
  aside?: string;

  /** Uputstvo. Vidi se samo kad je odeljak otvoren — sklopljen ne drži predavanja. */
  note?: React.ReactNode;

  open: boolean;
  onToggle: () => void;

  children: React.ReactNode;
}

export default function Section({
  id, title, count, tone, aside, note, open, onToggle, children
}: Props) {
  const ceka = (count ?? 0) > 0;

  const klase = ['panel'];
  if (tone === 'todo' && ceka) {
    klase.push('panel--todo');
  }
  if (tone === 'alert' && ceka) {
    klase.push('panel--alert');
  }

  return (
    <section className={klase.join(' ')} id={id}>
      <h2 className="panel__title">
        <button
          type="button"
          className="disc__head"
          aria-expanded={open}
          aria-controls={`${id}-telo`}
          onClick={onToggle}
        >
          <span className="disc__caret" aria-hidden="true">›</span>
          <span className="disc__title">
            {title}
            {aside && <span className="muted"> · {aside}</span>}
          </span>
          {ceka && <span className="count count--ceka">{count}</span>}
        </button>
      </h2>

      {open && (
        <div className="panel__body" id={`${id}-telo`}>
          {note && <p className="note">{note}</p>}
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Koji su odeljci otvoreni, i pamćenje toga između otvaranja ekrana.
 *
 * Podrazumevano stanje ZAVISI OD SADRŽAJA, a ne od spiska: odeljak u kome ima
 * posla otvara se sam, ostali stoje sklopljeni. Ono što je čovek jednom
 * sklopio ili otvorio pretiče podrazumevano — njegova odluka je novija od naše
 * pretpostavke.
 */
export function useSections(prefix: string) {
  const kljuc = `elbs.odeljci.${prefix}`;

  const [izbor, setIzbor] = useState<Record<string, boolean>>(() => {
    // Pregledač ume da zabrani lokalno čuvanje (privatni prozor, obrisani
    // podaci). Tada se ne pamti ništa, a ekran radi kao prvi put.
    try {
      const sirovo = localStorage.getItem(kljuc);
      return sirovo ? (JSON.parse(sirovo) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  const zapisi = useCallback((novo: Record<string, boolean>) => {
    try {
      localStorage.setItem(kljuc, JSON.stringify(novo));
    } catch {
      // Neupisano stanje nije greška koju čovek treba da vidi — odeljci samo
      // ne pamte izbor do sledećeg osvežavanja.
    }
  }, [kljuc]);

  /** Je li odeljak otvoren; `podrazumevano` važi dok čovek ne odluči drugačije. */
  const otvoren = useCallback(
    (id: string, podrazumevano: boolean) => izbor[id] ?? podrazumevano,
    [izbor]);

  const prebaci = useCallback((id: string, podrazumevano: boolean) => {
    setIzbor((trenutno) => {
      const novo = { ...trenutno, [id]: !(trenutno[id] ?? podrazumevano) };
      zapisi(novo);
      return novo;
    });
  }, [zapisi]);

  /** Otvori odeljak i skoči na njega — za brojke u sažetku iznad ekrana. */
  const skoci = useCallback((id: string) => {
    setIzbor((trenutno) => {
      const novo = { ...trenutno, [id]: true };
      zapisi(novo);
      return novo;
    });

    // Posle otvaranja, da skok ne padne na visinu koju odeljak još nema.
    window.setTimeout(
      () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      0);
  }, [zapisi]);

  return { otvoren, prebaci, skoci };
}
